import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { FacilityAttribute } from '../../firestore-models/facility-attribute';
import { ProjectLanguage } from '../../firestore-models/project-language';
import { ApiBaseAttribute, ApiSetFacilityAttributesRequest } from '../../api-client-module';

import { ApiClientService } from '../../api-client.service';
import { ConfigurationService } from '../../configuration.service';
import { SpinnerService } from '../../services/spinner.service';
import { AppConst } from '../../app-const';

import { FacilityAttributeDialogComponent } from './facility-attribute-dialog/facility-attribute-dialog.component';
import { MessageDialogComponent } from '../../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-facility-attribute',
  templateUrl: './facility-attribute.component.html',
  styleUrls: ['./facility-attribute.component.css'],
  standalone: false,
})
export class FacilityAttributeComponent implements OnInit, OnDestroy {

  //Facility Attributeコレクション
  private facilityAttributesCollection!: AngularFirestoreCollection<FacilityAttribute>;
  facilityAttributesObservable!: Observable<FacilityAttribute[]>;
  facilityAttributesSubscribe!: Subscription;
  //各属性情報に紐付くプロジェクトID
  projectId: string;
  //各属性情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  //属性情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'attr_id', 'name'];
  dataSource = new MatTableDataSource<FacilityAttribute>();
  selection = new SelectionModel<FacilityAttribute>(true, []);

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.facilityAttributesCollection = this.angularFirestore.collection<FacilityAttribute>(
      AppConst.FIRESTORE_FACILITY_ATTR, ref => ref.where('project_id', '==', this.projectId)
        .orderBy('attribute_id', 'asc'));
    this.facilityAttributesObservable = this.facilityAttributesCollection.valueChanges({ idField: 'id' });
    this.facilityAttributesSubscribe = this.facilityAttributesObservable.subscribe(facilityAttributes => {
      this.dataSource.data = this.addFilterValue(facilityAttributes);
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.facilityAttributesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: FacilityAttribute): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(facilityAttribute: FacilityAttribute): void {
    let apiFacilityAttribute: ApiBaseAttribute = {
      attribute_id: facilityAttribute.attribute_id,
      attribute_names: facilityAttribute.attribute_names,
      project_id: facilityAttribute.project_id,
      enabled: facilityAttribute.enabled
    };
    const dialogRef = this.dialog.open(FacilityAttributeDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, facilityAttribute: apiFacilityAttribute }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(FacilityAttributeDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, facilityAttribute: null }
    });
  }

  //削除
  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedFacilityAttributes: Array<ApiBaseAttribute> = new Array<ApiBaseAttribute>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedFacilityAttribute: ApiBaseAttribute = {
            attribute_id: row.attribute_id,
            attribute_names: row.attribute_names,
            project_id: row.project_id,
            enabled: row.enabled
          };
          selectedFacilityAttributes.push(selectedFacilityAttribute);
        }
      }
      if (!!result) {
        let request: ApiSetFacilityAttributesRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          attributes: selectedFacilityAttributes
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setFacilityAttributes(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'attr_id':
          return compare(a.attribute_id, b.attribute_id, isAsc);
        case 'name':
          const aName = a.attribute_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code })[0].name;
          const bName = b.attribute_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code })[0].name;
          return compare(aName, bName, isAsc);
        default:
          return 0;
      }
    });
  }

  addFilterValue(docs: Array<any>): Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.attribute_names) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
